const base = (import.meta.env.VITE_API_BASE as string) || `${import.meta.env.BASE_URL}api`;

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base.replace(/\/$/, '')}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || res.statusText);
  }
  return res.json();
}

export interface Profile {
  id: string;
  username: string | null;
  avatarUrl: string | null;
  isGuest: boolean;
  isAdmin: boolean;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  rating: number;
  highestRating: number;
  streak: number;
  quizGamesPlayed: number;
  bestTimedScore: number;
  bestStreak: number;
  correctAnswers: number;
  wrongAnswers: number;
  createdAt: string;
}

/** Leaderboard row — either one play (runs) or one user career (users). */
export interface LeaderboardRow {
  id: string;
  username: string | null;
  rating: number;
  score?: number;
  bestTimedScore: number;
  bestStreak: number;
  winRate: number;
  wins?: number;
  runId?: string;
  userId?: string;
  mode?: string;
  date?: string;
  durationSec?: number | null;
  ranked?: boolean;
}

export interface QuizQuestion {
  id: string;
  prompt: string;
  difficulty: string;
  category: string;
  choices: Array<{ index: number; text: string }>;
  correctChoiceIndex?: number;
  explanation?: string | null;
}

export interface AdminQuestion {
  id: string;
  prompt: string;
  choices: string[];
  correctChoiceIndex: number;
  difficulty: string;
  category: string;
  status: string;
  explanation: string | null;
  factIds: string[];
  correctCount: number;
  wrongCount: number;
  timeoutCount: number;
  servedCount: number;
  accuracy: number | null;
  createdAt: string;
}

export const api = {
  me: () => req<{ user: Profile | null }>('/me'),
  guest: () => req<{ user: Profile }>('/auth/guest', { method: 'POST', body: '{}' }),
  setUsername: (username: string) =>
    req<{ user: Profile }>('/auth/username', {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),
  leaderboard: (opts: {
    view?: 'runs' | 'career';
    game?: string;
    sort?: string;
    scope?: string;
    durationSec?: number;
    kind?: string;
  } = {}) => {
    const q = new URLSearchParams();
    if (opts.view) q.set('view', opts.view);
    if (opts.game) q.set('game', opts.game);
    if (opts.sort) q.set('sort', opts.sort);
    if (opts.scope) q.set('scope', opts.scope);
    if (opts.durationSec != null) q.set('durationSec', String(opts.durationSec));
    if (opts.kind) q.set('kind', opts.kind);
    const qs = q.toString();
    return req<{
      rows: LeaderboardRow[];
      view: string;
      mode: 'runs' | 'users';
      game?: string;
      sort?: string;
      scope?: string;
      durationSec?: number | null;
      durations?: number[];
      kind?: string;
    }>(`/leaderboard${qs ? `?${qs}` : ''}`);
  },
  history: (page = 1) =>
    req<{
      rows: Array<{
        id: string;
        mode: string;
        score: number;
        bestStreak: number;
        correct: number;
        wrong: number;
        date: string;
        durationSec: number;
        ranked: boolean;
        ratingDelta: number;
      }>;
      page: number;
      total: number;
    }>(`/history?page=${page}`),
  providers: () =>
    req<{ google: boolean; facebook: boolean; callbackGoogle?: string }>('/auth/providers'),
  startSolo: (body: {
    mode: 'solo_timed' | 'solo_lives' | 'solo_timebank' | 'solo_daily';
    durationSec?: number;
    locale?: string;
  }) =>
    req<{
      runId: string;
      mode: string;
      durationSec: number | null;
      endsAt: number | null;
      timeBankMs: number | null;
      livesLeft: number | null;
      score: number;
      streak: number;
      bestStreak?: number;
      correctCount?: number;
      wrong?: number;
      timeouts?: number;
      difficulty: string;
      question: QuizQuestion | null;
      deadlineAt: number | null;
      answerWindowMs: number | null;
      readingMs: number;
      dailyTotal?: number | null;
      dailyIndex?: number | null;
      onboarding?: boolean;
    }>('/quiz/solo', { method: 'POST', body: JSON.stringify(body) }),
  answerSolo: (runId: string, body: { questionId: string; choiceIndex: number | null }) =>
    req<{
      correct: boolean;
      timedOut: boolean;
      correctChoiceIndex: number;
      explanation: string | null;
      score: number;
      streak: number;
      bestStreak: number;
      livesLeft: number | null;
      difficulty: string;
      ended: boolean;
      endsAt: number | null;
      timeBankMs: number | null;
      revealMs: number;
      readingMs?: number;
      question: QuizQuestion | null;
      deadlineAt: number | null;
      answerWindowMs: number | null;
      correctCount?: number;
      wrong?: number;
      timeouts?: number;
      dailyTotal?: number | null;
      dailyIndex?: number | null;
    }>(`/quiz/solo/${runId}/answer`, { method: 'POST', body: JSON.stringify(body) }),
  nextSolo: (runId: string) =>
    req<{
      ended: boolean;
      question: QuizQuestion | null;
      deadlineAt: number | null;
      answerWindowMs: number | null;
      timeBankMs: number | null;
      endsAt: number | null;
      score: number;
      streak: number;
      bestStreak: number;
      livesLeft: number | null;
      difficulty: string;
      correctCount?: number;
      wrong?: number;
      timeouts?: number;
      dailyTotal?: number | null;
      dailyIndex?: number | null;
    }>(`/quiz/solo/${runId}/next`, { method: 'POST', body: '{}' }),
  dailyStatus: () =>
    req<{
      day: string;
      challengeKey: string;
      length: number;
      played: boolean;
      myScore: number | null;
      myBestStreak: number | null;
    }>('/quiz/daily'),
  adminQuestions: (params: Record<string, string | number | undefined> = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    return req<{ rows: AdminQuestion[]; total: number }>(`/admin/questions?${qs}`);
  },
  adminQuestion: (id: string) => req<AdminQuestion>(`/admin/questions/${id}`),
  adminPatchQuestion: (id: string, body: Record<string, unknown>) =>
    req<AdminQuestion>(`/admin/questions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  adminApprove: (id: string) =>
    req(`/admin/questions/${id}/approve`, { method: 'POST', body: '{}' }),
  adminReject: (id: string) =>
    req(`/admin/questions/${id}/reject`, { method: 'POST', body: '{}' }),
  adminCreateQuestion: (body: Record<string, unknown>) =>
    req(`/admin/questions`, { method: 'POST', body: JSON.stringify(body) }),
  adminStems: () =>
    req<{ rows: Array<{ stemKey: string; count: number; served: number; sample: string }> }>(
      '/admin/stems',
    ),
  adminRejectStem: (stemKey: string, keep = 12) =>
    req<{ stemKey: string; matched: number; rejected: number; kept: number }>(
      '/admin/stems/reject',
      { method: 'POST', body: JSON.stringify({ stemKey, keep }) },
    ),
  devAdmin: (secret: string) =>
    req<{ user: Profile }>('/auth/dev-admin', {
      method: 'POST',
      body: JSON.stringify({ secret }),
    }),
};

export function googleAuthUrl(): string {
  const apiBase = (import.meta.env.VITE_API_BASE as string) || `${import.meta.env.BASE_URL}api`;
  return `${apiBase.replace(/\/$/, '')}/auth/google/start`;
}

export function wsUrl(): string {
  const env = import.meta.env.VITE_WS_URL as string | undefined;
  if (env) return env;
  const basePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  return `${location.protocol}//${location.host}${basePath}/ws`;
}
